import React, { useState, useContext, useEffect, useRef } from "react";
import Ctx from "../../contexts/AppContext";
import { api } from "../../api";
import { inpS, btnP, btnSec, crd, sp, rad } from "../../constants/styles";
import Fld from "../common/Fld";
import LoadBtn from "../common/LoadBtn";
import useUnsavedWarning from "../../hooks/useUnsavedWarning";
import { canAddProperty } from "../../utils/plans";
import UpgradeModal from "../common/UpgradeModal";
import Ic from "../common/Ic";
import { safeRemoveMap } from "../../utils/safeRemoveMap";
import { uploadPhoto, deletePhoto } from "../../utils/supabaseClient";
import { containsPhoneNumber } from "../../utils/phoneFilter";
import { searchPlaces, summarizePlace } from "../../utils/geo";
import { RES_TYPES, COM_TYPES, isCommercial, ptLabel } from "../../utils/propertyTypes";

const SIVAKASI = [9.4533, 77.8024];
const TILE_URL = "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png";
const TILE_ATTR = '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>';

function LocationPicker({ lat, lng, onSelect, h, t }) {
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const leafletRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const L = await import("leaflet");
        await import("leaflet/dist/leaflet.css");
        // Fix default marker icons
        delete L.Icon.Default.prototype._getIconUrl;
        L.Icon.Default.mergeOptions({
          iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
          iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
          shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
        });
        if (!cancelled) { leafletRef.current = L; setReady(true); }
      } catch (e) { console.error("Leaflet load error:", e); }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!ready || !mapContainerRef.current || mapInstanceRef.current) return;
    const L = leafletRef.current;
    const center = lat && lng ? [parseFloat(lat), parseFloat(lng)] : SIVAKASI;
    const zoom = lat && lng ? 16 : 13;
    const map = L.map(mapContainerRef.current, { center, zoom, zoomControl: true });
    L.tileLayer(TILE_URL, { attribution: TILE_ATTR, maxZoom: 19 }).addTo(map);

    if (lat && lng) {
      markerRef.current = L.marker(center, { draggable: true }).addTo(map);
      markerRef.current.on("dragend", () => {
        const pos = markerRef.current.getLatLng();
        onSelect(pos.lat.toFixed(7), pos.lng.toFixed(7));
      });
    }

    map.on("click", (e) => {
      const { lat: cLat, lng: cLng } = e.latlng;
      if (markerRef.current) {
        markerRef.current.setLatLng([cLat, cLng]);
      } else {
        markerRef.current = L.marker([cLat, cLng], { draggable: true }).addTo(map);
        markerRef.current.on("dragend", () => {
          const pos = markerRef.current.getLatLng();
          onSelect(pos.lat.toFixed(7), pos.lng.toFixed(7));
        });
      }
      onSelect(cLat.toFixed(7), cLng.toFixed(7));
    });

    mapInstanceRef.current = map;
    return () => { safeRemoveMap(map); mapInstanceRef.current = null; markerRef.current = null; };
  }, [ready]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update marker when lat/lng change externally (e.g. "Use My Location")
  useEffect(() => {
    if (!mapInstanceRef.current || !leafletRef.current || !lat || !lng) return;
    const pos = [parseFloat(lat), parseFloat(lng)];
    if (markerRef.current) {
      markerRef.current.setLatLng(pos);
    } else {
      markerRef.current = leafletRef.current.marker(pos, { draggable: true }).addTo(mapInstanceRef.current);
      markerRef.current.on("dragend", () => {
        const p = markerRef.current.getLatLng();
        onSelect(p.lat.toFixed(7), p.lng.toFixed(7));
      });
    }
    mapInstanceRef.current.setView(pos, 16);
  }, [lat, lng]); // eslint-disable-line react-hooks/exhaustive-deps

  return <div>
    <div ref={mapContainerRef} style={{ width: "100%", height: 250, borderRadius: rad.md, overflow: "hidden", border: `1px solid ${h.bd}`, marginBottom: sp.sm }} />
    <p style={{ color: h.tt, fontSize: 10, margin: 0 }}>{t.tapToPin || "Tap on map to pin location, drag marker to adjust"}</p>
  </div>;
}

function PropFormPage({ edit }) {
  const { t, h, props, setProps, go, selPropId, flash, plan, refreshData } = useContext(Ctx);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const ex = edit ? props.find(p => p.id === selPropId) : null;
  const [addr, sa] = useState(ex?.addr || ""); const [rooms, sr] = useState(ex?.rooms?.toString() || ""); const [rent, sn] = useState(ex?.rent?.toString() || ""); const [dep, sd] = useState(ex?.dep?.toString() || ""); const [amen, sm] = useState(ex?.amen || []); const [err, se] = useState(""); const [customAmen, sCA] = useState("");
  const [propNo, setPropNo] = useState(ex?.property_label || "");
  const [description, setDescription] = useState(ex?.description || "");
  const [property_type, setPT] = useState(ex?.property_type || "apartment");
  // "Other" = a custom type the landlord typed (not one of the built-in
  // pills). Detected on edit when the saved type isn't a known enum.
  const isCustomExisting = !!ex?.property_type && !RES_TYPES.includes(ex.property_type) && !COM_TYPES.includes(ex.property_type);
  const [otherMode, setOtherMode] = useState(isCustomExisting);
  const [customType, setCustomType] = useState(isCustomExisting ? ex.property_type : "");
  const [area_sqft, setArea] = useState(ex?.area_sqft?.toString() || "");
  const [furnishing, setFurn] = useState(ex?.furnishing || "unfurnished");
  const [parking, setPark] = useState(ex?.parking || "");
  const [noCommon, setNoCommon] = useState(!!ex?.no_common_charges);
  const [lat, setLat] = useState(ex?.latitude?.toString() || "");
  const [lng, setLng] = useState(ex?.longitude?.toString() || "");
  const [locating, setLocating] = useState(false);
  const [photos, setPhotos] = useState(ex?.photos || []);
  const [uploading, setUploading] = useState(false);
  const photoInputRef = useRef(null);
  // Category is explicit state (was derived from the type) so the
  // top Residential/Commercial toggle still works while in "Other" mode,
  // where the typed value isn't a known commercial enum.
  const [cat, setCat] = useState(isCommercial(ex?.property_type) ? "commercial" : "residential");
  const isDirty = addr !== (ex?.addr || "") || rooms !== (ex?.rooms?.toString() || "") || rent !== (ex?.rent?.toString() || "") || property_type !== (ex?.property_type || "apartment") || propNo !== (ex?.property_label || "") || description !== (ex?.description || "");
  useUnsavedWarning(isDirty);
  const defaultA = ["AC", "Balcony", "WiFi", "Water", "Parking", "Furniture", "CCTV", "Generator", "Gym", "Lift", "Garden"];
  const allA = defaultA.concat(amen.filter(a => !defaultA.includes(a)));
  const addCustom = () => { const v = customAmen.trim(); if (v && !amen.includes(v)) { sm([...amen, v]); } sCA(""); };
  const radioS = (active) => ({ padding: "8px 16px", borderRadius: 20, border: `1px solid ${active ? h.ac : h.bd}`, background: active ? `${h.ac}15` : "transparent", color: active ? h.ac : h.sc, cursor: "pointer", fontSize: 12, fontWeight: active ? 600 : 400 });
  const pillS = (active) => ({ padding: "6px 14px", borderRadius: 20, border: `1px solid ${active ? h.ac : h.bd}`, background: active ? `${h.ac}15` : "transparent", color: active ? h.ac : h.sc, cursor: "pointer", fontSize: 11, fontWeight: active ? 600 : 400 });
  const lblS = { color: h.sc, fontSize: 12, fontWeight: 500, display: "block", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 };

  // Geocode the typed address → drop a pin. Lets landlords verify and
  // override the auto-geocode (which runs server-side at save time) by
  // previewing it BEFORE submission. If Nominatim returns multiple
  // matches we show them inline so the landlord picks the right one
  // (e.g. "Standard Colony" exists in multiple cities). After picking,
  // the existing LocationPicker pans + drops a draggable marker, so
  // they can still nudge it to the exact building.
  const [geoSearching, setGeoSearching] = useState(false);
  const [geoResults, setGeoResults] = useState([]);
  const findOnMap = async () => {
    const q = addr.trim();
    if (q.length < 3) { flash(t.enterAddressFirst || "Enter the property address first", "error"); return; }
    setGeoSearching(true);
    setGeoResults([]);
    try {
      const results = await searchPlaces(q, { limit: 5 });
      if (results.length === 0) {
        flash(t.noLocationMatch || "Couldn't find that on the map. Try a more specific address.", "error");
      } else if (results.length === 1) {
        // Single match — apply directly, no picker needed.
        const r = results[0];
        setLat(r.lat.toFixed(7));
        setLng(r.lng.toFixed(7));
        flash(t.locationFound || "📍 Pinned. Drag the marker to fine-tune.");
      } else {
        // Multiple matches — show picker. Landlord taps one.
        setGeoResults(results);
      }
    } catch (e) {
      flash(e.message || "Geocoding failed", "error");
    } finally {
      setGeoSearching(false);
    }
  };
  const pickGeoResult = (r) => {
    setLat(r.lat.toFixed(7));
    setLng(r.lng.toFixed(7));
    setGeoResults([]);
    flash(t.locationFound || "📍 Pinned. Drag the marker to fine-tune.");
  };

  const getLocation = () => {
    if (!navigator.geolocation) { flash(t.locationError, "error"); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { setLat(pos.coords.latitude.toFixed(7)); setLng(pos.coords.longitude.toFixed(7)); setLocating(false); flash(t.locationSaved); },
      () => { setLocating(false); flash(t.locationError, "error"); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const save = async () => {
    if (!addr || !rooms || !rent) { se(t.fillRequired); return; }
    if (otherMode && !customType.trim()) { se(t.enterCustomType || "Type the property type, or pick one above"); return; }
    // Reject phone numbers embedded in any free-text field. Prospects
    // reach the landlord through the inquiry flow; allowing a number
    // in the listing copy bypasses that and breaks the lead pipeline.
    // Catches digits, English number-words, and mixed forms — see
    // utils/phoneFilter.js.
    const phoneCheckTargets = [
      [propNo, "Door / Flat / Shop No."],
      [addr, "Address"],
      [description, "Description"],
      ...amen.map(a => [a, `Amenity "${a}"`]),
      [parking || "", "Parking"],
    ];
    for (const [val, label] of phoneCheckTargets) {
      if (containsPhoneNumber(val)) {
        se(`Please remove the phone number from ${label}. Prospects will reach you through the inquiry form.`);
        return;
      }
    }
    // Check plan limit for NEW properties only
    if (!edit && !canAddProperty(plan.type, props.length, plan.maxProperties)) {
      setShowUpgrade(true); return;
    }
    const r = Number(rent), d = Number(dep) || r * 3;
    const newProp = { id: edit ? selPropId : `p${Date.now()}`, addr, rooms: Number(rooms), rent: r, dep: d, amen, occ: edit ? ex.occ : false, tid: edit ? ex.tid : null, property_type, property_label: propNo || null, description: description.trim() || null, area_sqft: area_sqft ? Number(area_sqft) : null, furnishing: cat === "residential" ? furnishing : null, parking: parking || null, latitude: lat ? Number(lat) : null, longitude: lng ? Number(lng) : null, photos, no_common_charges: noCommon };
    // Save to server FIRST so we can surface plan-expired (402) or any
    // other server-side rejection before flashing "Added" and navigating.
    // Optimistic state update only happens after the server confirms.
    const res = await api.saveProperty(newProp);
    if (res && res.error) {
      // Server-side rejection (plan expired, validation, etc.). The
      // server's error message is already user-readable.
      se(res.error);
      return;
    }
    if (edit) setProps(props.map(p => p.id === selPropId ? { ...ex, ...newProp } : p)); else setProps([...props, newProp]);
    flash(edit ? t.updated : t.added); go(edit ? "propDetail" : "dash");
  };

  return <div style={{ paddingTop: 16 }}>
    <button onClick={() => go(edit ? "propDetail" : "dash")} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 13, marginBottom: 8 }}>&larr; {t.back}</button>
    <h2 style={{ color: h.tx, fontSize: 18, fontWeight: 700, margin: "0 0 16px" }}>{edit ? t.editProp : t.addProp}</h2>
    <div style={{ ...crd(h), padding: 20 }}>
      {err && <div style={{ color: h.rd, fontSize: 12, marginBottom: 10, padding: "8px 12px", background: `${h.rd}10`, borderRadius: 8 }}>{err}</div>}

      {/* Category: Residential / Commercial */}
      <label style={lblS}>{t.propertyType}</label>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <button type="button" onClick={() => { setCat("residential"); setOtherMode(false); setPT("apartment"); }} style={radioS(cat === "residential")}>{t.residentialProp}</button>
        <button type="button" onClick={() => { setCat("commercial"); setOtherMode(false); setPT("office"); }} style={radioS(cat === "commercial")}>{t.commercialProp}</button>
      </div>

      {/* Sub-type pills + "Other" escape hatch. Tapping Other reveals a
          text box so the landlord can name a type we don't list (e.g.
          "Banquet Hall"). Custom values are stored & displayed as typed. */}
      <label style={lblS}>{t.selectPropType}</label>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: otherMode ? 8 : 14 }}>
        {(cat === "residential" ? RES_TYPES : COM_TYPES).map(pt =>
          <button key={pt} type="button" onClick={() => { setOtherMode(false); setPT(pt); }} style={pillS(!otherMode && property_type === pt)}>{ptLabel(pt, t)}</button>)}
        <button type="button" onClick={() => { setOtherMode(true); setPT(customType.trim()); }} style={pillS(otherMode)}>{t.ptOther || "Other"}</button>
      </div>
      {otherMode && (
        <input
          value={customType}
          onChange={(e) => { const v = e.target.value; setCustomType(v); setPT(v.trim()); }}
          placeholder={t.ptOtherPlaceholder || "Type the property type — e.g. Banquet Hall"}
          maxLength={40}
          style={{ ...inpS(h), marginBottom: 14 }}
        />
      )}

      <Fld l={t.propNo || "No. (Door / Flat / Shop No.)"} v={propNo} set={setPropNo} h={h} ph="e.g. 12A, Flat 301, Shop 5" />
      <Fld l={t.address} v={addr} set={sa} h={h} /><Fld l={t.rooms} v={rooms} set={sr} h={h} type="number" /><Fld l={t.rent} v={rent} set={sn} h={h} type="number" />
      <Fld l={t.deposit} v={dep} set={sd} h={h} type="number" ph={rent ? `Default ₹${Number(rent) * 3}` : ""} />

      {/* Optional marketing description — shown on the public
          marketplace card + detail. Phone numbers are rejected at save
          (phoneCheckTargets) so prospects keep flowing through the
          inquiry form / lead pipeline. Capped at 500 chars (UI + server). */}
      <label style={lblS}>{t.propDescription || "Description (optional)"}</label>
      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value.slice(0, 500))}
        placeholder={t.descPlaceholder || "What makes this property attractive? e.g. East-facing 2BHK, 2 mins from bus stand, borewell + corporation water, newly painted…"}
        rows={3}
        maxLength={500}
        style={{ ...inpS(h), resize: "vertical", fontFamily: "inherit", marginBottom: 2 }}
      />
      <div style={{ color: h.tt || h.sc, fontSize: 10, textAlign: "right", marginBottom: 12 }}>{description.length}/500</div>

      {/* Commercial-only: area + parking */}
      {cat === "commercial" && <>
        <Fld l={t.areaSqft} v={area_sqft} set={setArea} h={h} type="number" ph="e.g. 1200" />
        <Fld l={t.parkingSpaces} v={parking} set={setPark} h={h} ph="e.g. 2 car + 1 bike" />
      </>}

      {/* Residential-only: furnishing */}
      {cat === "residential" && <>
        <label style={lblS}>{t.furnishing}</label>
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          {[["unfurnished", t.unfurnished], ["semi", t.semiFurnished], ["furnished", t.furnished]].map(([v, l]) =>
            <button key={v} type="button" onClick={() => setFurn(v)} style={pillS(furnishing === v)}>{l}</button>)}
        </div>
      </>}

      {/* Common charges opt-out */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", marginBottom: 8, borderTop: `1px solid ${h.bd}30`, borderBottom: `1px solid ${h.bd}30` }}>
        <div>
          <div style={{ color: h.tx, fontSize: 13, fontWeight: 500 }}>No Common Charges</div>
          <div style={{ color: h.sc, fontSize: 10, marginTop: 2 }}>Skip common charges for this property (e.g., independent house)</div>
        </div>
        <button onClick={() => setNoCommon(!noCommon)} style={{ background: noCommon ? h.ac : h.ib || "#eee", border: "none", color: noCommon ? "#fff" : h.sc, padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: "pointer", minWidth: 50 }}>{noCommon ? "ON" : "OFF"}</button>
      </div>

      <label style={{ color: h.sc, fontSize: 12, fontWeight: 500, display: "block", marginBottom: 6 }}>{t.amenities}</label>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>{allA.map(a => <button key={a} onClick={() => sm(amen.includes(a) ? amen.filter(x => x !== a) : [...amen, a])}
        style={{ padding: "6px 14px", borderRadius: 20, border: `1px solid ${amen.includes(a) ? h.ac : h.bd}`, background: amen.includes(a) ? `${h.ac}15` : "transparent", color: amen.includes(a) ? h.ac : h.sc, cursor: "pointer", fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}>{a}{amen.includes(a) && <span style={{ fontSize: 14 }}>×</span>}</button>)}</div>
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <input value={customAmen} onChange={e => sCA(e.target.value)} placeholder={t.addCustomAmen} style={{ ...inpS(h), flex: 1, marginBottom: 0 }} onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addCustom(); } }} />
        <button onClick={addCustom} style={{ background: `${h.ac}15`, border: `1px solid ${h.ac}`, color: h.ac, padding: "8px 16px", borderRadius: 10, cursor: "pointer", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" }}>{"+ " + t.add}</button></div>
      {/* Photos */}
      <label style={lblS}>{t.photos || "Photos"}</label>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: sp.sm }}>
        {photos.map((url, i) => (
          <div key={i} style={{ position: "relative", width: 72, height: 72, borderRadius: rad.sm, overflow: "hidden", border: `1px solid ${h.bd}` }}>
            <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            <button onClick={() => { deletePhoto(url); setPhotos(photos.filter((_, j) => j !== i)); }}
              style={{ position: "absolute", top: 2, right: 2, width: 20, height: 20, borderRadius: 10, background: "rgba(0,0,0,0.6)", color: "#fff", border: "none", cursor: "pointer", fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}>{"\u00D7"}</button>
          </div>
        ))}
        {photos.length < 10 && <button onClick={() => photoInputRef.current?.click()} disabled={uploading}
          style={{ width: 72, height: 72, borderRadius: rad.sm, border: `2px dashed ${h.bd}`, background: h.ib, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", cursor: uploading ? "default" : "pointer", gap: 2 }}>
          {uploading ? <span style={{ fontSize: 10, color: h.ts }}>...</span> : <>
            <Ic n="plus" s={18} c={h.ts} />
            <span style={{ fontSize: 9, color: h.ts }}>{t.addPhoto || "Add"}</span>
          </>}
        </button>}
      </div>
      <input ref={photoInputRef} type="file" accept="image/*" multiple hidden onChange={async (e) => {
        const files = Array.from(e.target.files);
        if (!files.length) return;
        const propId = edit ? selPropId : `p${Date.now()}`;
        setUploading(true);
        try {
          const urls = [];
          for (const f of files.slice(0, 10 - photos.length)) {
            const url = await uploadPhoto(f, propId);
            urls.push(url);
          }
          setPhotos(prev => [...prev, ...urls]);
          flash(`${urls.length} photo${urls.length > 1 ? "s" : ""} uploaded!`);
        } catch (err) {
          flash(err.message || "Upload failed", "error");
        }
        setUploading(false);
        e.target.value = "";
      }} />
      <p style={{ color: h.tt, fontSize: 10, margin: `0 0 ${sp.lg}px` }}>{t.maxPhotos || "Max 10 photos. Tap + to add, × to remove."}</p>

      {/* Location */}
      <label style={lblS}>{t.location}</label>
      <div style={{ display: "flex", gap: 8, marginBottom: sp.sm, flexWrap: "wrap" }}>
        <button type="button" onClick={getLocation} disabled={locating} style={{ ...btnSec(h), flex: "1 1 140px", fontSize: 12, padding: "10px 12px", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
          <Ic n="navigation" s={14} c={h.ac} /> {locating ? t.locating : t.useMyLocation}
        </button>
        {/* "Find on map" → geocode the typed address. Lets the landlord
            verify what auto-geocode will pin BEFORE saving, and pick
            from multiple matches if Nominatim returns ambiguous results
            (e.g. a colony name that exists in multiple cities). */}
        <button type="button" onClick={findOnMap} disabled={geoSearching || !addr.trim()} style={{ ...btnSec(h), flex: "1 1 140px", fontSize: 12, padding: "10px 12px", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, opacity: (geoSearching || !addr.trim()) ? 0.6 : 1, cursor: (geoSearching || !addr.trim()) ? "not-allowed" : "pointer" }}>
          <span>{"🗺️"}</span>
          <span>{geoSearching ? (t.searching || "Searching…") : (t.findOnMap || "Find on map")}</span>
        </button>
        {(lat || lng) && <button type="button" onClick={() => { setLat(""); setLng(""); }} style={{ ...btnSec(h), padding: "10px 14px", fontSize: 12, color: h.rd, borderColor: `${h.rd}30` }}>
          <Ic n="x" s={14} c={h.rd} />
        </button>}
      </div>

      {/* Geocode result picker — shown when "Find on map" returns
          multiple candidates. Tapping one sets the pin AND closes the
          picker. The landlord can still drag the marker afterwards to
          nail the exact building. */}
      {geoResults.length > 0 && (
        <div style={{
          marginBottom: sp.sm,
          padding: `${sp.sm}px ${sp.md}px`,
          borderRadius: rad.sm,
          border: `1px solid ${h.ac}40`,
          background: `${h.ac}06`,
        }}>
          <div style={{ color: h.ac, fontSize: 11, fontWeight: 700, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.4 }}>
            {t.pickLocationMatch || "Pick the right match"}
          </div>
          {geoResults.map(r => {
            const { title, subtitle } = summarizePlace(r);
            return (
              <button
                key={r.place_id}
                type="button"
                onClick={() => pickGeoResult(r)}
                style={{
                  width: "100%", textAlign: "left",
                  padding: `${sp.xs + 2}px ${sp.xs}px`,
                  background: "transparent",
                  border: "none",
                  borderBottom: `1px solid ${h.bd}40`,
                  cursor: "pointer",
                  display: "flex", alignItems: "flex-start", gap: 8,
                }}
              >
                <span style={{ fontSize: 13, marginTop: 1 }}>{"📍"}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: h.tx, fontSize: 12, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</div>
                  {subtitle && <div style={{ color: h.sc, fontSize: 10, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{subtitle}</div>}
                </div>
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => setGeoResults([])}
            style={{
              background: "none", border: "none",
              color: h.sc, cursor: "pointer",
              fontSize: 11, fontWeight: 600,
              padding: "6px 0 0", marginTop: 2,
            }}
          >
            {t.cancel || "Cancel"}
          </button>
        </div>
      )}

      {(lat && lng) && <div style={{ background: `${h.gn}10`, borderRadius: rad.sm, padding: `${sp.xs}px ${sp.sm}px`, marginBottom: sp.sm, display: "flex", alignItems: "center", gap: sp.xs }}>
        <Ic n="check-circle" s={12} c={h.gn} />
        <span style={{ color: h.gn, fontSize: 11, fontWeight: 500 }}>{lat}, {lng}</span>
      </div>}
      <LocationPicker lat={lat} lng={lng} onSelect={(la, lo) => { setLat(la); setLng(lo); }} h={h} t={t} />
      <div style={{ height: sp.md }} />

      <div style={{ display: "flex", gap: 10 }}><LoadBtn onClick={save} label={t.save} style={{ ...btnP(h), flex: 1 }} /><button onClick={() => go(edit ? "propDetail" : "dash")} style={{ ...btnSec(h), flex: 1 }}>{t.cancel}</button></div>
    </div>
    {showUpgrade && <UpgradeModal h={h} t={t} used={props.length} limit={plan.maxProperties} currentPlan={plan.type} onClose={() => setShowUpgrade(false)} onUpgradeSuccess={async () => { await refreshData(); flash(t.billingSuccess || "Plan Upgraded!"); }} />}
  </div>;
}

export default PropFormPage;
